import { useHasForms } from "../../hooks/useHasForms";
import FormExportPickerModal from "../Forms/FormExportPickerModal";
import {
  ArrowPathIcon,
  ArrowUpOnSquareIcon,
  ClipboardDocumentListIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ChatBubbleBottomCenterTextIcon,
  ChatBubbleLeftEllipsisIcon,
  DocumentArrowDownIcon,
  DocumentCheckIcon,
  DocumentDuplicateIcon,
  MinusIcon,
  PencilIcon,
  SparklesIcon,
  XMarkIcon,
  PlayIcon,
  PauseIcon,
  CheckIcon,
  StarIcon,
} from "@heroicons/react/24/outline";
import {
  BoldIcon,
  ChevronDownIcon,
  ItalicIcon,
  ListBulletIcon,
  MicrophoneIcon,
  NumberedListIcon,
  PaintBrushIcon,
  StrikethroughIcon,
  UnderlineIcon,
  MagnifyingGlassIcon,
  StarIcon as StarSolidIcon,
} from "@heroicons/react/24/solid";
import * as Sentry from "@sentry/react";
import { Editor as EditorProps } from "@tiptap/react";
import axios from "axios";
import cn from "classnames";
import { Loader2 } from "lucide-react";
import moment from "moment";
import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCaseCategories } from "../../context/CaseCategoriesContext";
import {
  CaseStatus,
  PreferenceStatuteStateType,
  RedactionType,
  SubscriptionStatusType,
} from "../../db/schema";
import { useIsMobile } from "../../hooks/useIsMobile";
import { convertHtmlToPlainText } from "../../utilities/converHtmlToPlainText";
import Button from "../common/Button";
import CustomToast from "../common/CustomToast";
import { convertAudioToMono } from "../common/convertAudioToMono";
import IconButton from "../common/IconButton";
import Input from "../common/Input";
import Tooltip from "../common/Tooltip";
import CaseCategoryMenu from "./CaseCategoryMenu";
import FindBar from "./FindBar";
import { stripPlaceholders } from "./ReplaceText";
import Dropdown from "../Settings/Dropdown";
import TextInput from "../Settings/TextInput";
import TextArea from "../Settings/TextArea";
import DatePicker from "../Settings/DatePicker";
import Toggle from "../Settings/Toggle";
import { useUserRoles } from "../../utilities/roleUtils";
import TemplateSelector from "../common/TemplateSelector";

interface EditorMenuBarProps {
  editor: EditorProps | null;
  disabled?: boolean;
  caseId: string | undefined;
  score: number | null | undefined;
  onChange: (data: { content: { data: string }; caseId?: string }) => void;
  onTranscribeStateChange: (isTranscribing: boolean) => void;
  activeTab: "narrative" | "transcription";
  findSlotRef?: React.Ref<HTMLDivElement>;
  /** Transcript find bar state (owned by Editor); toggle is undefined when no transcript is showing */
  transcriptFindOpen?: boolean;
  onToggleTranscriptFind?: () => void;
  isProcessing?: boolean;
  caseTranscription?: any;
  caseAudioMetadata?: any;
  caseStatus?: (typeof CaseStatus.enumValues)[number];
  currentTranscription?: any;
  activeFileId?: string;
  isAiGenerated?: boolean;
  caseRedactions?: RedactionType[];
  onCaseDeleted?: (caseId: string) => void;
  proofreadEnabled?: boolean;
  onToggleProofread?: () => void;
  placeholdersHidden?: boolean;
}

const iconBtnStyles = "size-4";

const copySuccessToast = () => {
  toast.success("Copied to clipboard");
};

const copyToClipboardFallback = (text: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      resolve();
    } catch (err) {
      reject(err);
    }
  });
};

const EditorMenuBar = function ({
  editor,
  disabled,
  caseId,
  score,
  onChange,
  onTranscribeStateChange,
  activeTab,
  findSlotRef,
  transcriptFindOpen,
  onToggleTranscriptFind,
  isProcessing,
  caseTranscription,
  caseAudioMetadata,
  caseStatus,
  currentTranscription,
  activeFileId,
  isAiGenerated,
  caseRedactions,
  onCaseDeleted,
  proofreadEnabled,
  onToggleProofread,
  placeholdersHidden,
}: EditorMenuBarProps) {
  const { isRecords } = useUserRoles();
  const isRecordsUser = isRecords(); // Create stable reference
  const { toggleFavorite, mutationVersion } = useCaseCategories();

  // Favorite state for the current case (per-user). Loaded from the same
  // endpoint the category bar uses; toggled through the shared context so the
  // sidebar Favorites count and case-card stars stay in sync. Re-runs on
  // mutationVersion so the star updates immediately when the case is favorited
  // elsewhere (e.g. dragged onto sidebar Favorites).
  // Agency data retention policy. When deleteOnExport is on, the export
  // dialogs show a pre-checked "Delete case upon export" checkbox the
  // officer can uncheck to keep the case. Individual users (no agency)
  // get deleteOnExport: false, hiding the feature entirely.
  const [retentionPolicy, setRetentionPolicy] = useState<{
    deleteOnExport: boolean;
    mode: "trash" | "permanent" | null;
  } | null>(null);
  const [deleteCaseOnExport, setDeleteCaseOnExport] = useState(true);
  useEffect(() => {
    let cancelled = false;
    axios
      .get("/api/settings/data-retention", { withCredentials: true })
      .then((res) => {
        if (!cancelled) setRetentionPolicy(res.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const [isFavorite, setIsFavorite] = useState(false);
  useEffect(() => {
    if (!caseId) {
      setIsFavorite(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`/api/cases/${caseId}/categories`, { withCredentials: true })
      .then((res) => {
        if (!cancelled) setIsFavorite(!!res.data.isFavorite);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [caseId, mutationVersion]);

  const handleToggleFavorite = () => {
    if (!caseId) return;
    const next = !isFavorite;
    setIsFavorite(next);
    toggleFavorite(caseId, next);
  };
  const [isTextColorPickerOpen, setIsTextColorPickerOpen] = useState(false);
  const [isHighlightColorPickerOpen, setIsHighlightColorPickerOpen] =
    useState(false);
  const [isHeadingDropdownOpen, setIsHeadingDropdownOpen] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordingAction, setRecordingAction] = useState<
    "pure-insert" | "enhanced-insert" | null
  >(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingCancelledRef = useRef<boolean>(false);
  const cursorPositionRef = useRef<number>(0);
  const [preserveFormatting, setPreserveFormatting] = useState(false);
  // Preserve-formatting toggles for the file/email exports. Unlike the
  // clipboard copy (which defaults to plain text), these default ON so the
  // exported document keeps its rich formatting; turning them off flattens
  // headings/bold to plain text while keeping bullet and numbered lists.
  const [preservePdfFormatting, setPreservePdfFormatting] = useState(true);
  const [preserveWordFormatting, setPreserveWordFormatting] = useState(true);
  const [preserveEmailFormatting, setPreserveEmailFormatting] = useState(true);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [dictationTemplateId, setDictationTemplateId] = useState<string | null>(
    null,
  );
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [devices, setDevices] = useState<{ deviceId: string; label: string }[]>(
    [],
  );
  const [exportTranscriptionFormat, setExportTranscriptionFormat] = useState<
    "letter" | "legal" | "txt" | "docx"
  >("letter");
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [customCaseId, setCustomCaseId] = useState<string>("");
  const [customTitle, setCustomTitle] = useState<string>("");
  const [customHeader, setCustomHeader] = useState<string>("");
  const [customDate, setCustomDate] = useState<string>(
    moment().format("YYYY-MM-DD"),
  );
  const [includeTimestamps, setIncludeTimestamps] = useState<boolean>(true);
  // Narrative PDF export — separate state per field so the user can tweak
  // header/footer without affecting the transcription PDF dialog above.
  const [pdfCustomCaseId, setPdfCustomCaseId] = useState<string>("");
  const [pdfCustomTitle, setPdfCustomTitle] = useState<string>("");
  const [pdfCustomHeader, setPdfCustomHeader] = useState<string>("");
  const [pdfCustomFooter, setPdfCustomFooter] = useState<string>("");
  const [pdfCustomDate, setPdfCustomDate] = useState<string>(
    moment().format("YYYY-MM-DD"),
  );
  // Whether to include the title / case ID / date blocks in the exported PDF.
  // Default off; users opt in to any of them per export.
  const [includePdfTitle, setIncludePdfTitle] = useState(false);
  const [includePdfCaseId, setIncludePdfCaseId] = useState(false);
  const [includePdfDate, setIncludePdfDate] = useState(false);
  // The "Generated: <date> at <time>" line at the bottom of every PDF page.
  // Off by default; users can opt in per export.
  const [includePdfGeneratedTimestamp, setIncludePdfGeneratedTimestamp] =
    useState(false);
  const [isNarrativePdfExporting, setIsNarrativePdfExporting] = useState(false);
  const [showSearchReplace, setShowSearchReplace] = useState(false);
  const [showReplaceInput, setShowReplaceInput] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [replaceTerm, setReplaceTerm] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  // "Export with redactions" — controls whether the export handlers swap each
  // redaction text out for a solid black bar in the produced content. Defaults
  // off; the checkbox only appears in export dialogs when the case actually
  // has redactions.
  const [applyRedactionsOnExport, setApplyRedactionsOnExport] = useState(false);
  // Sub-options revealed only while "Export with redactions" is on:
  // - show tags: prints the redaction's tag (e.g. "[suspect]") in white inside
  //   the black bar so readers know what kind of information was removed.
  // - footnotes: numbers each justified redaction and appends a "Footnotes
  //   (legal justification for redaction)" section citing the statute.
  const [redactionShowTags, setRedactionShowTags] = useState(false);
  const [redactionShowFootnotes, setRedactionShowFootnotes] = useState(false);
  const hasRedactions = !!caseRedactions && caseRedactions.length > 0;
  const taggedRedactionCount = (caseRedactions || []).filter((r) =>
    r.tag?.trim(),
  ).length;
  const justifiedRedactionCount = (caseRedactions || []).filter(
    (r) => r.statute?.trim() || r.justification?.trim(),
  ).length;
  const [resetKey, setResetKey] = useState<number>(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const emailDialogRef = useRef<HTMLDialogElement>(null);
  const wordDialogRef = useRef<HTMLDialogElement>(null);
  // "Export to a form": the list of forms to fill out from this case.
  const formPickerRef = useRef<HTMLDialogElement>(null) as React.RefObject<HTMLDialogElement>;
  // "Fill Forms" is only offered to someone who has a form to fill.
  const hasForms = useHasForms();
  const pdfDialogRef = useRef<HTMLDialogElement>(null);
  const transcriptionPdfDialogRef = useRef<HTMLDialogElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const {
    subscriptionStatus,
    subscriptionCurrentPeriodEnd,
    refreshSubscriptionStatus,
    checkAuthStatus,
    reportPreferences,
  } = useAuth();
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsHeadingDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    refreshSubscriptionStatus();
  }, [refreshSubscriptionStatus]);

  // Cleanup recording timer on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  // Handler to close search and clear all state
  const handleCloseSearch = useCallback(() => {
    setShowSearchReplace(false);
    setShowReplaceInput(false);
    setSearchTerm("");
    setReplaceTerm("");
    setCaseSensitive(false);
    // Clear the search from the editor to remove highlighting
    editor?.commands.setSearchTerm("");
    editor?.commands.setReplaceTerm("");
    editor?.commands.setCaseSensitive(false);
  }, [editor]);

  // Keyboard shortcut for search (Cmd+F / Ctrl+F)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // The transcription tab runs its own find bar over the transcript
      if (activeTab === "transcription") return;
      if ((e.metaKey || e.ctrlKey) && e.key === "f") {
        e.preventDefault();
        setShowSearchReplace(true);
      }
      // ESC to close search panel or replace mode
      if (e.key === "Escape" && showSearchReplace) {
        if (showReplaceInput) {
          setShowReplaceInput(false);
        } else {
          handleCloseSearch();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showSearchReplace, showReplaceInput, handleCloseSearch, activeTab]);

  // Check permission and enumerate devices
  useEffect(() => {
    const checkPermissionStatus = async () => {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        });
        setHasPermission(permissionStatus.state === "granted");

        // Listen for permission changes
        permissionStatus.addEventListener("change", () => {
          setHasPermission(permissionStatus.state === "granted");
        });

        // If permission is already granted, enumerate devices
        if (permissionStatus.state === "granted") {
          await enumerateDevices();
        }
      } catch (error) {
        console.error("Error checking permission:", error);
        setHasPermission(false);
      }
    };

    checkPermissionStatus();
  }, []);

  // Intercept full-report copy (Ctrl+C / Cmd+C / right-click copy)
  // Uses keydown to prevent copy before ProseMirror handles it
  useEffect(() => {
    if (!editor) return;

    const isFullReportSelected = () => {
      const { from, to } = editor.state.selection;
      const docSize = editor.state.doc.content.size;

      // Check if entire doc is selected
      if (from <= 1 && to >= docSize - 1) return true;

      // Check if body (everything after H1) is selected
      const firstNode = editor.state.doc.firstChild;
      if (firstNode?.type.name === "heading" && firstNode.attrs?.level === 1) {
        const bodyStart = firstNode.nodeSize;
        if (from <= bodyStart + 1 && to >= docSize - 1) return true;
      }

      return false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "c" && isFullReportSelected()) {
        e.preventDefault();
        e.stopPropagation();
        setDeleteCaseOnExport(true);
        dialogRef.current?.showModal();
      }
    };

    const handleCopy = (e: ClipboardEvent) => {
      if (isFullReportSelected()) {
        e.preventDefault();
        e.stopPropagation();
        setDeleteCaseOnExport(true);
        dialogRef.current?.showModal();
      }
    };

    const editorElement = editor.view.dom;
    editorElement.addEventListener("keydown", handleKeyDown, true);
    editorElement.addEventListener("copy", handleCopy, true);
    return () => {
      editorElement.removeEventListener("keydown", handleKeyDown, true);
      editorElement.removeEventListener("copy", handleCopy, true);
    };
  }, [editor]);

  const enumerateDevices = async () => {
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const audioInputDevices = allDevices
        .filter((device) => device.kind === "audioinput")
        .map((device) => ({
          deviceId: device.deviceId,
          label:
            device.label || `Microphone (${device.deviceId.slice(0, 5)}...)`,
        }));
      setDevices(audioInputDevices);

      if (audioInputDevices.length > 0) {
        const defaultDevice =
          audioInputDevices.find((device) => device.deviceId === "default") ||
          audioInputDevices[0];
        setSelectedDeviceId(defaultDevice.deviceId);
      }
    } catch (error) {
      console.error("Error enumerating devices:", error);
    }
  };

  const requestPermissionAndGetDevices = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      stream.getTracks().forEach((track) => track.stop());
      setHasPermission(true);
      await enumerateDevices();
    } catch (error) {
      console.error(
        "Error requesting permission or enumerating devices:",
        error,
      );
      setHasPermission(false);
    }
  };

  function extractTitle(content: string): string {
    const match = content.match(/<h1[^>]*>(.*?)<\/h1>/);
    return match ? match[1] : "";
  }

  // Type guard to check if it's Amazon Transcribe format
  const isTranscribeFormat = (
    data: any,
  ): data is {
    jobName: string;
    accountId: string;
    status: string;
    results: any;
  } => {
    return data && "results" in data && "jobName" in data;
  };

  // Reset function to clear custom settings and force TextInputs to show defaults
  const resetCustomSettings = () => {
    setTimeout(() => {
      setCustomCaseId("");
      setCustomTitle("");
      setCustomHeader("");
      setCustomDate(moment().format("YYYY-MM-DD"));
      setIncludeTimestamps(true);
      setResetKey((prev) => prev + 1); // Force TextInput components to re-render
    }, 500);
  };

  const handleTranscriptionPDFExport = async () => {
    const transcriptionForExport = currentTranscription || caseTranscription;
    if (
      !transcriptionForExport ||
      !isTranscribeFormat(transcriptionForExport)
    ) {
      toast.error("No transcription available for export");
      return;
    }

    setIsPdfExporting(true);
    try {
      const defaultTitle = editor ? extractTitle(editor.getHTML()) : "";

      // Choose endpoint based on format
      const endpoint =
        exportTranscriptionFormat === "txt"
          ? "/api/transcription-txt"
          : "/api/transcription-pdf";
      const responseType =
        exportTranscriptionFormat === "txt" ? "text" : "blob";

      const response = await axios.post(
        endpoint,
        {
          caseId,
          fileId: activeFileId,
          format: exportTranscriptionFormat,
          customCaseId: customCaseId || caseId,
          customTitle: customTitle || defaultTitle,
          customHeader: customHeader,
          customDate: customDate,
          includeTimestamps: includeTimestamps,
        },
        {
          withCredentials: true,
          responseType: responseType,
        },
      );
      if (exportTranscriptionFormat === "txt") {
        // Handle TXT file download
        const blob = new Blob([response.data], { type: "text/plain" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `transcription-${caseId || "report"}-${moment().format(
          "YYYY-MM-DD",
        )}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("Transcription TXT downloaded successfully");
      } else {
        // Handle PDF / Word file download (the PDF route renders both)
        const isDocx = exportTranscriptionFormat === "docx";
        const blob = new Blob([response.data], {
          type: isDocx ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "application/pdf",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `transcription-${caseId || "report"}-${moment().format(
          "YYYY-MM-DD",
        )}.${isDocx ? "docx" : "pdf"}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success(`Transcription ${isDocx ? "Word document" : "PDF"} downloaded successfully`);
      }

      transcriptionPdfDialogRef.current?.close();

      // Reset custom settings to defaults after successful export
      resetCustomSettings();
    } catch (error) {
      console.error("Error generating transcription file:", error);
      Sentry.captureException(error, {
        tags: {
          component: "EditorMenuBar",
          action: "handleTranscriptionPDFExport",
        },
        extra: { caseId, format: exportTranscriptionFormat },
      });
      checkAuthStatus();
      toast.error(
        error.response?.status === 401
          ? "Please log in to export transcription"
          : "Failed to generate transcription file",
      );
    } finally {
      setIsPdfExporting(false);
    }
  };

  const CA_SB524_DISCLAIMER =
    "This report was written either fully or in part using artificial intelligence." +
    " I affirm that this report, generated with the assistance of Police Narratives AI, has been thoroughly reviewed and edited by me to ensure its accuracy in representing my recollection of the reported events." +
    " I acknowledge the potential legal implications of this statement and, if required, am prepared to testify to its accuracy.";

  const shouldAppendDisclaimer =
    isAiGenerated &&
    reportPreferences?.statute_state ===
      PreferenceStatuteStateType.Values.California;

  const appendCaliforniaDisclaimer = (
    html: string,
    plainText: string,
  ): { html: string; plainText: string } => {
    if (!shouldAppendDisclaimer) return { html, plainText };
    if (
      plainText.includes(
        "This report was written either fully or in part using artificial intelligence.",
      )
    )
      return { html, plainText };
    return {
      html: html + `<p>${CA_SB524_DISCLAIMER}</p>`,
      plainText: plainText + "\n\n" + CA_SB524_DISCLAIMER,
    };
  };

  // Replace every occurrence of each redaction's text with a solid black bar.
  // We use box-drawing block characters (█) sized to the original word length
  // so the visual bar matches the redaction. The HTML version wraps the bars
  // in a black-on-black inline span so even a "View Source" or paste-as-text
  // recovery shows just block characters — the original text is gone.
  //
  // Caveat: the redaction text is matched verbatim against the HTML/plaintext
  // string. If the report wraps the text across inline tags (e.g.
  // "John <strong>Smith</strong>"), the HTML pass won't match — but Bedrock
  // returns redactions as verbatim substrings of the plain content, and
  // narrative reports don't normally split PII mid-word with formatting, so
  // in practice this is rare.
  const REDACTION_BAR_CHAR = "█"; // █
  const REDACTION_INLINE_STYLE =
    "background-color:#000;color:#000;font-family:monospace;letter-spacing:0.05em;";
  // White-on-black style used when the bar shows a reader-facing tag like
  // "[suspect]" instead of solid blocks.
  const REDACTION_TAG_STYLE =
    "background-color:#000;color:#fff;padding:1px 6px;white-space:nowrap;";
  // Private-use-area markers that survive the narrative-pdf endpoint's
  // inline-tag stripping (which removes <span>/<sup> and collapses \s+).
  // The server's PDF renderer parses these back out and draws white tag text
  // and small raised footnote numbers over the black bar:
  //   \uE002 label [\uE000 digits] \uE003  -> tagged black bar
  //   \uE000 digits \uE001              -> standalone superscript number
  //   \uE004                            -> space inside a tag label
  // (mirrored in server/src/routes/exportRouter.ts)
  const PDF_TAG_OPEN = "\uE002";
  const PDF_TAG_CLOSE = "\uE003";
  const PDF_FN_OPEN = "\uE000";
  const PDF_FN_CLOSE = "\uE001";
  const PDF_TAG_SPACE = "\uE004";
  const escapeForRegex = (s: string) =>
    s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const escapeHtml = (s: string) =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  const SUPERSCRIPT_DIGITS = ["⁰", "¹", "²", "³", "⁴", "⁵", "⁶", "⁷", "⁸", "⁹"];
  const toSuperscript = (n: number) =>
    String(n)
      .split("")
      .map((d) => SUPERSCRIPT_DIGITS[Number(d)])
      .join("");

  // Footnote numbering for the "legal justification" footnotes. Redactions
  // sharing the same statute + justification share one footnote number (like
  // the reference sample where every privacy redaction is footnote 1 and the
  // confidential-source redaction is footnote 2). Numbers are assigned in
  // first-appearance order within the report so footnote 1 is the first one a
  // reader encounters. Items without a statute/justification get no number.
  type RedactionFootnotes = {
    numberOf: Map<string, number>; // redaction id -> footnote number
    list: { n: number; statute?: string; justification?: string }[];
  };
  const buildRedactionFootnotes = (plainText: string): RedactionFootnotes => {
    const numberOf = new Map<string, number>();
    const list: RedactionFootnotes["list"] = [];
    const justified = (caseRedactions || []).filter(
      (r) => r.text?.trim() && (r.statute?.trim() || r.justification?.trim()),
    );
    const lower = plainText.toLowerCase();
    const byAppearance = [...justified].sort((a, b) => {
      const ia = lower.indexOf(a.text.trim().toLowerCase());
      const ib = lower.indexOf(b.text.trim().toLowerCase());
      return (ia === -1 ? Infinity : ia) - (ib === -1 ? Infinity : ib);
    });
    const keyToNumber = new Map<string, number>();
    for (const r of byAppearance) {
      const key = `${r.statute?.trim() || ""}|${r.justification?.trim() || ""}`;
      let n = keyToNumber.get(key);
      if (!n) {
        n = keyToNumber.size + 1;
        keyToNumber.set(key, n);
        list.push({
          n,
          statute: r.statute?.trim() || undefined,
          justification: r.justification?.trim() || undefined,
        });
      }
      numberOf.set(r.id, n);
    }
    return { numberOf, list };
  };

  const footnoteLine = (f: { statute?: string; justification?: string }) =>
    `${f.statute ? `(${f.statute}) ` : ""}${f.justification || ""}`.trim();

  // "rich" targets clipboard/Word/email HTML (real spans + <sup>); "pdf"
  // targets the narrative-pdf endpoint, which strips inline tags, so it
  // encodes tags/footnotes with the PUA markers above instead.
  const redactHtml = (
    html: string,
    footnotes: RedactionFootnotes,
    mode: "rich" | "pdf",
  ): string => {
    if (!caseRedactions?.length) return html;
    let out = html;
    for (const r of caseRedactions) {
      const t = r.text?.trim();
      if (!t) continue;
      const bar = REDACTION_BAR_CHAR.repeat(Math.max(t.length, 3));
      const n = redactionShowFootnotes ? footnotes.numberOf.get(r.id) : undefined;
      const tag = redactionShowTags && r.tag?.trim() ? r.tag.trim() : undefined;
      let replacement: string;
      if (mode === "pdf") {
        replacement = tag
          ? `${PDF_TAG_OPEN}[${tag.replace(/\s+/g, PDF_TAG_SPACE)}]${
              n ? `${PDF_FN_OPEN}${n}` : ""
            }${PDF_TAG_CLOSE}`
          : `${bar}${n ? `${PDF_FN_OPEN}${n}${PDF_FN_CLOSE}` : ""}`;
      } else {
        replacement = tag
          ? `<span style="${REDACTION_TAG_STYLE}">[${escapeHtml(tag)}]${
              n ? `<sup>&nbsp;${n}</sup>` : ""
            }</span>`
          : `<span style="${REDACTION_INLINE_STYLE}">${bar}</span>${
              n ? `<sup>${n}</sup>` : ""
            }`;
      }
      const re = new RegExp(escapeForRegex(t), "gi");
      out = out.replace(re, () => replacement);
    }
    if (redactionShowFootnotes && footnotes.list.length) {
      const header =
        mode === "pdf"
          ? `<p>Footnotes (legal justification for redaction)</p>`
          : `<p><em>Footnotes (legal justification for redaction)</em></p>`;
      const lines = footnotes.list
        .map((f) =>
          mode === "pdf"
            ? `<p>${PDF_FN_OPEN}${f.n}${PDF_FN_CLOSE} ${escapeHtml(footnoteLine(f))}</p>`
            : `<p><sup>${f.n}</sup> ${escapeHtml(footnoteLine(f))}</p>`,
        )
        .join("");
      out += `<p></p>${header}${lines}`;
    }
    return out;
  };

  const redactPlainText = (
    text: string,
    footnotes: RedactionFootnotes,
  ): string => {
    if (!caseRedactions?.length) return text;
    let out = text;
    for (const r of caseRedactions) {
      const t = r.text?.trim();
      if (!t) continue;
      const bar = REDACTION_BAR_CHAR.repeat(Math.max(t.length, 3));
      const n = redactionShowFootnotes ? footnotes.numberOf.get(r.id) : undefined;
      const tag = redactionShowTags && r.tag?.trim() ? r.tag.trim() : undefined;
      const replacement = `${tag ? `[${tag}]` : bar}${n ? toSuperscript(n) : ""}`;
      const re = new RegExp(escapeForRegex(t), "gi");
      out = out.replace(re, () => replacement);
    }
    if (redactionShowFootnotes && footnotes.list.length) {
      out +=
        "\n\nFootnotes (legal justification for redaction)\n" +
        footnotes.list
          .map((f) => `${toSuperscript(f.n)} ${footnoteLine(f)}`)
          .join("\n");
    }
    return out;
  };

  // Flattens rich formatting for exports whose "Preserve Formatting" toggle is
  // off: section subheadings (h2–h6) become plain paragraphs and bold/italic/
  // underline/strikethrough marks are unwrapped. The document title (h1),
  // bullet/numbered lists (ul/ol/li), links, and redaction spans are kept so
  // the output is "plain" without collapsing lists into a single bullet.
  const flattenFormatting = (html: string): string =>
    html
      .replace(/<(h[2-6])[^>]*>([\s\S]*?)<\/\1>/gi, "<p>$2</p>")
      .replace(/<\/?(strong|b|em|i|u|s)[^>]*>/gi, "");

  // Convenience wrapper used by the export handlers — keeps the
  // checkbox/redaction logic in one place. The PDF handler passes
  // mode "pdf" so tags/footnotes are encoded for the server renderer.
  // Also drops the {{…}} placeholders when the officer has hidden them, so
  // what they export matches what they see in the editor.
  const applyExportRedactions = (
    html: string,
    plainText: string,
    mode: "rich" | "pdf" = "rich",
  ): { html: string; plainText: string } => {
    if (placeholdersHidden) {
      html = stripPlaceholders(html);
      plainText = stripPlaceholders(plainText);
    }
    if (!applyRedactionsOnExport || !hasRedactions) return { html, plainText };
    const footnotes = redactionShowFootnotes
      ? buildRedactionFootnotes(plainText)
      : { numberOf: new Map<string, number>(), list: [] };
    return {
      html: redactHtml(html, footnotes, mode),
      plainText: redactPlainText(plainText, footnotes),
    };
  };

  const handleCopyToClipboard = async () => {
    if (editor) {
      try {
        let content = editor.getHTML();
        let textToAudit = convertHtmlToPlainText(content);
        ({ html: content, plainText: textToAudit } = appendCaliforniaDisclaimer(
          content,
          textToAudit,
        ));
        ({ html: content, plainText: textToAudit } = applyExportRedactions(
          content,
          textToAudit,
        ));

        await axios.post(
          "/api/audits",
          {
            caseId,
            content: textToAudit, // Always send plain text to the audit API
            score,
          },
          {
            withCredentials: true,
          },
        );

        try {
          if (preserveFormatting) {
            // Use ClipboardItem API for rich text copying
            // This allows pasting with formatting into Word, Notes, etc.
            // Ensure empty paragraphs render as visible blank lines.
            // Browsers sanitize clipboard HTML and strip empty elements,
            // so &nbsp; forces the paragraph to be treated as real content.
            const richContent = content
              .replace(/<p><\/p>/g, "<p>&nbsp;</p>")
              .replace(/<p><br\s*\/?><\/p>/g, "<p>&nbsp;</p>");
            const htmlBlob = new Blob([richContent], { type: "text/html" });
            const plainTextBlob = new Blob([textToAudit], {
              type: "text/plain",
            });

            if (navigator.clipboard && navigator.clipboard.write) {
              await navigator.clipboard.write([
                new ClipboardItem({
                  "text/html": htmlBlob,
                  "text/plain": plainTextBlob,
                }),
              ]);
            } else {
              // Fallback to older method for browsers without clipboard.write
              const tempDiv = document.createElement("div");
              tempDiv.innerHTML = richContent;
              tempDiv.style.position = "absolute";
              tempDiv.style.left = "-9999px";
              document.body.appendChild(tempDiv);

              const selection = window.getSelection();
              const range = document.createRange();
              range.selectNodeContents(tempDiv);

              selection?.removeAllRanges();
              selection?.addRange(range);
              document.execCommand("copy");
              document.body.removeChild(tempDiv);
            }
          } else {
            // Just copy plain text
            await navigator.clipboard.writeText(textToAudit);
          }
        } catch (err) {
          // Fallback for mobile devices
          await copyToClipboardFallback(
            preserveFormatting ? content : textToAudit,
          );
        }

        dialogRef.current?.close();
        copySuccessToast();
        await maybeDeleteAfterExport();
      } catch (error) {
        console.error("Error in copy process:", error);
        Sentry.captureException(error, {
          tags: {
            component: "EditorMenuBar",
            action: "handleCopyToClipboard",
          },
          extra: { caseId },
        });
        checkAuthStatus();
        toast.error(
          error.response?.status === 401
            ? "Please log in to export content"
            : "Failed to export content",
        );
      }
    }
  };

  // Narrative PDF export. Mirrors handleExportWord's pre-processing (CA
  // disclaimer + optional redaction blackout via █ runs) and then hands the
  // finished HTML to the server-side jsPDF renderer in exportRouter. The
  // server applies the user's custom header/footer/title around the report.
  const handleExportNarrativePdf = async () => {
    if (!editor || !caseId) return;
    setIsNarrativePdfExporting(true);
    try {
      let content = editor.getHTML();
      let textToAudit = convertHtmlToPlainText(content);
      ({ html: content, plainText: textToAudit } = appendCaliforniaDisclaimer(
        content,
        textToAudit,
      ));
      ({ html: content, plainText: textToAudit } = applyExportRedactions(
        content,
        textToAudit,
        "pdf",
      ));

      // Log to the audit trail like the other export paths so report copies
      // and PDFs share the same provenance record.
      await axios.post(
        "/api/audits",
        { caseId, content: textToAudit, score },
        { withCredentials: true },
      );

      const response = await axios.post(
        "/api/narrative-pdf",
        {
          caseId,
          format: "letter",
          contentHtml: preservePdfFormatting
            ? content
            : flattenFormatting(content),
          customCaseId: pdfCustomCaseId || undefined,
          customTitle: pdfCustomTitle || undefined,
          customHeader: pdfCustomHeader || undefined,
          customFooter: pdfCustomFooter || undefined,
          customDate: pdfCustomDate || undefined,
          includeTitle: includePdfTitle,
          includeCaseId: includePdfCaseId,
          includeDate: includePdfDate,
          includeGeneratedTimestamp: includePdfGeneratedTimestamp,
        },
        { withCredentials: true, responseType: "blob" },
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const titleForFile =
        pdfCustomTitle || extractTitle(content) || `report-${caseId}`;
      const safeName = titleForFile.replace(/[^a-zA-Z0-9-_ ]/g, "").trim();
      link.download = `${safeName || "report"}-${moment().format("YYYY-MM-DD")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      pdfDialogRef.current?.close();
      toast.success("PDF downloaded");
      await maybeDeleteAfterExport();
    } catch (error) {
      console.error("Error generating narrative PDF:", error);
      Sentry.captureException(error, {
        tags: { component: "EditorMenuBar", action: "handleExportNarrativePdf" },
        extra: { caseId },
      });
      checkAuthStatus();
      toast.error(
        error.response?.status === 401
          ? "Please log in to export PDF"
          : "Failed to generate PDF",
      );
    } finally {
      setIsNarrativePdfExporting(false);
    }
  };

  const handleExportWord = async () => {
    if (editor) {
      try {
        let content = editor.getHTML();
        let textToAudit = convertHtmlToPlainText(content);
        ({ html: content, plainText: textToAudit } = appendCaliforniaDisclaimer(
          content,
          textToAudit,
        ));
        ({ html: content, plainText: textToAudit } = applyExportRedactions(
          content,
          textToAudit,
        ));

        await axios.post(
          "/api/audits",
          {
            caseId,
            content: textToAudit,
            score,
          },
          {
            withCredentials: true,
          },
        );

        const wordContent = preserveWordFormatting
          ? content
          : flattenFormatting(content);

        const wordHtml = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
<head>
<meta charset='utf-8'>
<style>
  body { font-family: Calibri, sans-serif; font-size: 11pt; line-height: 1.15; }
  p { margin: 0; }
  ul, ol { margin-top: 0; margin-bottom: 0; }
  h1 { font-size: 16pt; font-weight: bold; }
  h2 { font-size: 14pt; font-weight: bold; }
  h3 { font-size: 12pt; font-weight: bold; }
</style>
</head>
<body>${wordContent}</body>
</html>`;

        const blob = new Blob(["\ufeff", wordHtml], {
          type: "application/msword",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const title = extractTitle(content);
        const fileName = title
          ? title.replace(/[^a-zA-Z0-9-_ ]/g, "").trim()
          : `report-${caseId || "export"}-${moment().format("YYYY-MM-DD")}`;
        link.download = `${fileName}.doc`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        wordDialogRef.current?.close();
        toast.success("Word document exported");
        await maybeDeleteAfterExport();
      } catch (error) {
        console.error("Error exporting Word document:", error);
        Sentry.captureException(error, {
          tags: {
            component: "EditorMenuBar",
            action: "handleExportWord",
          },
          extra: { caseId },
        });
        checkAuthStatus();
        toast.error(
          error.response?.status === 401
            ? "Please log in to export content"
            : "Failed to export Word document",
        );
      }
    }
  };

  const validateEmail = (email: string) => {
    if (!email) {
      setEmailError("Email is required");
      return false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError("Email is invalid");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleEmailExport = async () => {
    if (editor && validateEmail(emailValue)) {
      setIsLoading(true);
      try {
        let htmlBody = editor.getHTML();
        let textBody = convertHtmlToPlainText(htmlBody);
        ({ html: htmlBody, plainText: textBody } = appendCaliforniaDisclaimer(
          htmlBody,
          textBody,
        ));
        ({ html: htmlBody, plainText: textBody } = applyExportRedactions(
          htmlBody,
          textBody,
        ));
        const title = extractTitle(htmlBody);
        if (!preserveEmailFormatting) {
          htmlBody = flattenFormatting(htmlBody);
        }

        await axios.post(
          "/api/email",
          {
            caseId,
            title,
            htmlBody,
            textBody,
            score,
            emailTo: emailValue,
          },
          {
            withCredentials: true,
          },
        );

        toast.success("Email sent successfully");
        emailDialogRef.current?.close();
        setEmailValue("");
        await maybeDeleteAfterExport();
      } catch (error) {
        console.error("Error in email process:", error);
        Sentry.captureException(error, {
          tags: {
            component: "EditorMenuBar",
            action: "handleEmailExport",
          },
          extra: { caseId, emailTo: emailValue },
        });
        checkAuthStatus();
        toast.error(
          error.response?.status === 401
            ? "Please log in to send email"
            : "Failed to send email",
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Shared "Export with redactions" block used by all four export dialogs
  // (clipboard, email, Word, PDF). The two sub-options only appear once the
  // master toggle is on, and intentionally render as selectable cards rather
  // than more Toggles so they read as refinements of the master switch.
  // When disabled (older cases whose redactions predate tags/justifications),
  // hovering shows a tooltip explaining how to enable the option.
  const renderRedactionOption = (
    selected: boolean,
    disabled: boolean,
    title: string,
    description: string,
    onClick: () => void,
    disabledReason?: string,
  ) => {
    const option = (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn(
          "w-full flex items-start gap-2 rounded-md border p-2 text-left transition",
          disabled
            ? "border-base-300 bg-base-200 opacity-50 pointer-events-none"
            : selected
              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/40"
              : "border-base-300 bg-base-100 hover:border-blue-300 dark:hover:border-blue-800",
        )}
      >
        <span
          className={cn(
            "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border",
            selected
              ? "border-blue-500 bg-blue-500 text-white"
              : "border-gray-300 dark:border-base-300 bg-base-100 text-transparent",
          )}
        >
          <CheckIcon className="size-3" strokeWidth={3} />
        </span>
        <span className="block">
          <span className="block text-xs font-semibold text-base-content">
            {title}
          </span>
          <span className="block text-[11px] text-base-content/60 leading-snug">
            {description}
          </span>
        </span>
      </button>
    );
    // Disabled buttons swallow hover events in some browsers, so the tooltip
    // hangs off a wrapper span (the button gets pointer-events-none above).
    // The tooltip must mount inside the <dialog> — these are opened with
    // showModal(), whose top layer covers anything portaled to body.
    return disabled && disabledReason ? (
      <Tooltip
        overlay={disabledReason}
        placement="top"
        getTooltipContainer={(node) =>
          node.closest("dialog") || document.body
        }
      >
        <span className="block w-full cursor-not-allowed">{option}</span>
      </Tooltip>
    ) : (
      option
    );
  };

  const redactionExportSection = hasRedactions ? (
    <div className="mb-4">
      <Toggle
        label={`Export with redactions (${caseRedactions!.length} item${caseRedactions!.length === 1 ? "" : "s"})`}
        checked={applyRedactionsOnExport}
        onChange={(e) => setApplyRedactionsOnExport(e.target.checked)}
      />
      <p className="text-xs text-base-content/60 m-0 mt-0">
        Replaces each flagged item with a solid black bar.
      </p>
      {applyRedactionsOnExport && (
        <div className="not-prose mt-2 grid gap-1.5 rounded-lg border border-base-300 bg-base-200 p-2">
          {renderRedactionOption(
            redactionShowTags,
            taggedRedactionCount === 0,
            "Show tags on bars",
            taggedRedactionCount === 0
              ? "No items have tags yet."
              : `Prints a label like [suspect] inside the bar so readers know what was removed (${taggedRedactionCount} of ${caseRedactions!.length} item${caseRedactions!.length === 1 ? "" : "s"} tagged).`,
            () => setRedactionShowTags((v) => !v),
            'This case\'s redactions were created before tags existed. Press "Re-identify redactions" in the Redactions tab to generate them, or add tags to items by hand.',
          )}
          {renderRedactionOption(
            redactionShowFootnotes,
            justifiedRedactionCount === 0,
            "Legal justification footnotes",
            justifiedRedactionCount === 0
              ? "No items have a statutory justification yet."
              : `Numbers each redaction and appends footnotes citing the statutory basis (${justifiedRedactionCount} of ${caseRedactions!.length} item${caseRedactions!.length === 1 ? "" : "s"}).`,
            () => setRedactionShowFootnotes((v) => !v),
            'This case\'s redactions were created before legal justifications existed. Press "Re-identify redactions" in the Redactions tab to generate them, or add a justification to items by hand.',
          )}
        </div>
      )}
    </div>
  ) : null;

  // Shared "Delete case upon export" block for the four export dialogs
  // (clipboard, email, Word, PDF). Only rendered when the agency's data
  // retention policy enables delete-on-export; pre-checked, but the officer
  // may uncheck it to keep this case.
  const showDeleteOnExport =
    !!retentionPolicy?.deleteOnExport && caseStatus !== "discarded";
  const deleteOnExportSection = showDeleteOnExport ? (
    <div className="mb-4 not-prose rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-3">
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={deleteCaseOnExport}
          onChange={(e) => setDeleteCaseOnExport(e.target.checked)}
          className="checkbox checkbox-sm mt-0.5 [--chkbg:theme(colors.red.600)] border-red-400 dark:border-red-700"
        />
        <span className="block">
          <span className="block text-sm font-semibold text-red-700 dark:text-red-300">
            Delete case upon export
          </span>
          <span className="block text-xs text-red-700/80 dark:text-red-300/80 leading-snug">
            {deleteCaseOnExport
              ? retentionPolicy?.mode === "permanent"
                ? "Per your agency's data retention policy, this case — including audio, transcripts, and all attached files — will be permanently deleted once exported. This cannot be undone."
                : "Per your agency's data retention policy, this case will be moved to the trash bin once exported."
              : "This case will be kept."}
          </span>
        </span>
      </label>
    </div>
  ) : null;

  // Called at the end of each export handler's success path. A failed export
  // throws before reaching this, so the case is never deleted on failure.
  const maybeDeleteAfterExport = async () => {
    if (!showDeleteOnExport || !deleteCaseOnExport || !caseId) return;
    try {
      const { data } = await axios.post(
        `/api/cases/${caseId}/export-delete`,
        {},
        { withCredentials: true },
      );
      toast.success(
        data.mode === "permanent"
          ? "Case exported and permanently deleted"
          : "Case exported and moved to trash",
      );
      onCaseDeleted?.(caseId);
    } catch (error) {
      console.error("Error deleting case after export:", error);
      Sentry.captureException(error, {
        tags: { component: "EditorMenuBar", action: "exportDelete" },
        extra: { caseId },
      });
      toast.error("Export succeeded but the case could not be deleted");
    }
  };

  const navigate = useNavigate();

  const restoreCase = async () => {
    if (caseId) {
      try {
        const response = await axios.patch(
          `/api/cases/${caseId}/restore`,
          {},
          { withCredentials: true },
        );
        if (response.status === 200) {
          toast.success("Case restored successfully");
          navigate(`/cases/${caseId}`);
        }
      } catch (error) {
        console.error("Error restoring case:", error);
        Sentry.captureException(error, {
          tags: {
            component: "EditorMenuBar",
            action: "restoreCase",
          },
          extra: { caseId },
        });
        checkAuthStatus();
        toast.error(
          error.response?.status === 401
            ? "Please log in to restore case"
            : "Failed to restore case",
        );
      }
    }
  };

  const getTimeRemaining = () => {
    if (
      !subscriptionCurrentPeriodEnd &&
      subscriptionStatus === SubscriptionStatusType.enum.trialing
    )
      return "";
    const now = moment();
    const end = moment(subscriptionCurrentPeriodEnd);
    const diff = end.diff(now, "hours");

    if (diff < 24) {
      return `${diff} hours left in trial`;
    }
    return `${Math.ceil(diff / 24)} days left in trial`;
  };

  type HeadingLevel = 1 | 2 | 3;
  interface HeadingOption {
    name: ReactElement;
    level: HeadingLevel | 0;
  }
  const headingLevels: HeadingOption[] = [
    { name: <span>Paragraph</span>, level: 0 },
    { name: <h1>Heading 1</h1>, level: 1 },
    { name: <h2>Heading 2</h2>, level: 2 },
    { name: <h3>Heading 3</h3>, level: 3 },
  ];

  if (editor == null) {
    return null;
  }

  const getCurrentHeading = () => {
    const heading = headingLevels.find((h) =>
      h.level === 0
        ? editor.isActive("paragraph")
        : editor.isActive("heading", { level: h.level }),
    );
    return heading ? heading.name : <span>Paragraph</span>;
  };

  const applyHeading = (level: HeadingOption["level"]) => {
    if (level === 0) {
      editor.chain().focus().setParagraph().run();
    } else {
      editor.chain().focus().toggleHeading({ level }).run();
    }
    setIsHeadingDropdownOpen(false);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const showCursorIndicator = () => {
    // Remove any existing cursor indicator
    const existing = document.querySelector(".dictation-cursor-indicator");
    if (existing) existing.remove();

    // Add blink animation style if not exists
    if (!document.querySelector("#dictation-cursor-style")) {
      const style = document.createElement("style");
      style.id = "dictation-cursor-style";
      style.textContent = `
        @keyframes dictation-blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    // Get the cursor position and editor element
    const { view } = editor;
    const from = cursorPositionRef.current;
    const coords = view.coordsAtPos(from);

    // Find the ProseMirror editor element to position relative to
    const editorElement = document.querySelector(".ProseMirror");
    if (!editorElement) return;

    const editorRect = editorElement.getBoundingClientRect();

    // Create cursor indicator element positioned absolutely within the editor
    const cursor = document.createElement("div");
    cursor.className = "dictation-cursor-indicator";
    cursor.style.cssText = `
      position: absolute;
      width: 2px;
      height: 20px;
      background-color: #3b82f6;
      z-index: 9999;
      animation: dictation-blink 1s infinite;
      pointer-events: none;
    `;
    // Position relative to the editor element
    cursor.style.left = `${coords.left - editorRect.left}px`;
    cursor.style.top = `${coords.top - editorRect.top}px`;

    // Make sure the editor has position relative for absolute positioning to work
    (editorElement as HTMLElement).style.position = "relative";
    editorElement.appendChild(cursor);
  };

  const hideCursorIndicator = () => {
    const cursor = document.querySelector(".dictation-cursor-indicator");
    if (cursor) cursor.remove();
  };

  const startRecording = async (action: "pure-insert" | "enhanced-insert") => {
    try {
      // Capture cursor position BEFORE UI changes
      cursorPositionRef.current = editor.state.selection.from;

      setIsRecording(true);
      setRecordingAction(action);
      setRecordingDuration(0);
      setIsPaused(false);
      recordingCancelledRef.current = false;
      editor.setEditable(false);
      onTranscribeStateChange(true);

      // Show cursor indicator after a brief delay to allow recording bar to render
      // This ensures the visual position accounts for the UI shift
      setTimeout(() => {
        showCursorIndicator();
      }, 100);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: selectedDeviceId
          ? {
              deviceId: { exact: selectedDeviceId },
            }
          : true,
      });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        // Clear timer
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }

        // If recording was cancelled, don't process
        if (recordingCancelledRef.current) {
          hideCursorIndicator();
          setIsTranscribing(false);
          onTranscribeStateChange(false);
          editor.setEditable(true);
          return;
        }

        setIsTranscribing(true);
        const toastId = toast.custom(
          <CustomToast variant="loading" title="Transcribing audio..." />,
          { id: "transcribe", duration: Infinity },
        );

        try {
          const audioBlob = new Blob(audioChunksRef.current, {
            type: "audio/wav",
          });

          // Convert audio to mono WAV format
          const convertedAudio = await convertAudioToMono(audioBlob);

          const formData = new FormData();
          formData.append("audio", convertedAudio, "recording.wav");

          // Get the full report context and insert cursor marker
          const cursorPosText = " {TEXT SNIPPET WILL BE ADDED HERE} ";
          const { head } = editor.state.selection;
          const beforeCursor = editor.state.doc.textBetween(0, head);
          const afterCursor = editor.state.doc.textBetween(
            head,
            editor.state.doc.content.size,
          );
          const fullReport = beforeCursor + cursorPosText + afterCursor;

          formData.append("context", fullReport);
          formData.append("action", action);
          if (caseId) {
            formData.append("caseId", caseId);
          }
          if (dictationTemplateId) {
            formData.append("reportTemplateId", dictationTemplateId);
          }

          const response = await axios.post(
            "/api/transcribe/insert",
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            },
          );

          if (response.data.content) {
            // Check for invalid audio error before inserting content
            if (response.data.content.toLowerCase().includes("invalid audio")) {
              const transcriptionError = new Error(response.data.content);
              (transcriptionError as any).audioUrl = response.data.audioUrl;
              throw transcriptionError;
            }
            editor.setEditable(true);
            editor?.commands.insertContent(response.data.content);
            onChange({ content: { data: editor.getHTML() }, caseId });
            toast.success("Audio transcribed successfully", {
              id: "transcribe",
              duration: 5000,
            });
          }
        } catch (error) {
          console.error("Error transcribing audio:", error);
          Sentry.captureException(error, {
            tags: {
              component: "EditorMenuBar",
              action: "startRecording",
            },
            extra: { action },
          });

          const errorMessage =
            error?.message || "Invalid audio, please try again";

          // Get S3 URL from the error response if available
          const s3Url = error?.audioUrl || null;

          if (s3Url) {
            toast.custom(
              <CustomToast
                variant="error"
                title={errorMessage}
                description="Please download your audio file and try again"
                actionLabel="Download audio file"
                onAction={() => {
                  window.open(s3Url, "_blank");
                }}
                showCloseButton
                onClose={() => toast.dismiss(toastId)}
                size="md"
              />,
              { id: toastId, duration: Infinity },
            );
          } else {
            toast.custom(<CustomToast variant="error" title={errorMessage} />, {
              id: toastId,
            });
          }

          editor.setEditable(true);
        } finally {
          hideCursorIndicator();
          setIsTranscribing(false);
          onTranscribeStateChange(false);
        }

        // Stop all tracks
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();

      // Start duration timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      hideCursorIndicator();
      setIsRecording(false);
      setRecordingAction(null);
      setIsTranscribing(false);
      onTranscribeStateChange(false);
      editor.setEditable(true);
      checkAuthStatus();
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingAction(null);
      setIsPaused(false);
    }
  };

  const pauseRecording = () => {
    if (!mediaRecorderRef.current) return;
    if (isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      // Resume timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } else {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      // Pause timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };

  const cancelRecording = () => {
    // Set cancelled flag so onstop handler knows not to process
    recordingCancelledRef.current = true;
    hideCursorIndicator();
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsRecording(false);
    setRecordingAction(null);
    setRecordingDuration(0);
    setIsPaused(false);
    editor.setEditable(true);
    onTranscribeStateChange(false);
  };

  const isButtonDisabled = disabled || activeTab === "transcription";

  const renderActionButton = () => {
    // If case is discarded, show Restore button
    if (caseStatus === "discarded") {
      return (
        <Button
          tabIndex={0}
          data-tour="editor-restore"
          className={cn(
            "fixed md:relative bottom-0 left-1/2 -translate-x-1/2 md:translate-x-0 md:left-auto md:bottom-auto",
            isMobile && "!w-full rounded-none",
          )}
          variant="primary"
          size="sm"
          onClick={() => {
            restoreCase();
          }}
        >
          <div className="flex items-center">
            <ArrowPathIcon className="size-5 mr-1" />
            <p>Restore</p>
          </div>
        </Button>
      );
    }

    // If user is a records user, always show export button (never disabled)
    if (isRecordsUser) {
      return (
        <div className="dropdown dropdown-end">
          <Button
            tabIndex={0}
            data-tour="editor-export"
            className={cn(
              "fixed md:relative bottom-0 left-1/2 -translate-x-1/2 md:translate-x-0 md:left-auto md:bottom-auto",
              isMobile && "!w-full rounded-none",
            )}
            variant="primary"
            size="sm"
          >
            <div className="flex items-center">
              <ArrowUpOnSquareIcon className="size-6 mr-1 hidden xl:inline-block" />
              <p>Export</p>
            </div>
          </Button>
          <ul
            tabIndex={0}
            className="dropdown-content menu bg-base-100 rounded-lg z-[1] w-52 p-2 shadow mt-2"
          >
            <li>
              <p
                onClick={() => {
                  setDeleteCaseOnExport(true);
                  dialogRef.current?.showModal();
                }}
                className="hover:bg-gray-100 dark:hover:bg-base-200"
              >
                <DocumentDuplicateIcon className="size-4" />
                Copy to Clipboard
              </p>
            </li>
            <li>
              <p
                onClick={() => {
                  setDeleteCaseOnExport(true);
                  wordDialogRef.current?.showModal();
                }}
                className="hover:bg-gray-100 dark:hover:bg-base-200"
              >
                <DocumentArrowDownIcon className="size-4" />
                Word Document
              </p>
            </li>
            <li>
              <p
                onClick={() => {
                  setDeleteCaseOnExport(true);
                  pdfDialogRef.current?.showModal();
                }}
                className="hover:bg-gray-100 dark:hover:bg-base-200"
              >
                <DocumentArrowDownIcon className="size-4" />
                PDF Document
              </p>
            </li>
            <li
              onClick={() => {
                setDeleteCaseOnExport(true);
                emailDialogRef.current?.showModal();
              }}
            >
              <p className="hover:bg-gray-100 dark:hover:bg-base-200">
                <ChatBubbleBottomCenterTextIcon className="size-4" />
                E-mail
              </p>
            </li>
            {caseId && hasForms && (
              <li onClick={() => formPickerRef.current?.showModal()}>
                <p className="hover:bg-gray-100 dark:hover:bg-base-200">
                  <ClipboardDocumentListIcon className="size-4" />
                  Fill Forms
                </p>
              </li>
            )}
            {(currentTranscription || caseTranscription) &&
              isTranscribeFormat(currentTranscription || caseTranscription) && (
                <li
                  onClick={() => transcriptionPdfDialogRef.current?.showModal()}
                >
                  <p className="hover:bg-gray-100 dark:hover:bg-base-200">
                    <DocumentArrowDownIcon className="size-4" />
                    Transcription
                  </p>
                </li>
              )}
          </ul>
        </div>
      );
    }

    // For non-records users, follow existing logic
    if (!disabled || isProcessing) {
      return (
        <div className="dropdown dropdown-end">
          <Button
            tabIndex={0}
            data-tour="editor-export"
            className={cn(
              "fixed md:relative bottom-0 left-1/2 -translate-x-1/2 md:translate-x-0 md:left-auto md:bottom-auto",
              isMobile && "!w-full rounded-none",
            )}
            variant="primary"
            size="sm"
            disabled={disabled}
          >
            <div className="flex items-center">
              <ArrowUpOnSquareIcon className="size-6 mr-1 hidden xl:inline-block" />
              <p>Export</p>
            </div>
          </Button>
          <ul
            tabIndex={0}
            className="dropdown-content menu bg-base-100 rounded-lg z-[1] w-52 p-2 shadow mt-2"
          >
            <li>
              <p
                onClick={() => {
                  setDeleteCaseOnExport(true);
                  dialogRef.current?.showModal();
                }}
                className="hover:bg-gray-100 dark:hover:bg-base-200"
              >
                <DocumentDuplicateIcon className="size-4" />
                Copy to Clipboard
              </p>
            </li>
            <li>
              <p
                onClick={() => {
                  setDeleteCaseOnExport(true);
                  wordDialogRef.current?.showModal();
                }}
                className="hover:bg-gray-100 dark:hover:bg-base-200"
              >
                <DocumentArrowDownIcon className="size-4" />
                Word Document
              </p>
            </li>
            <li>
              <p
                onClick={() => {
                  setDeleteCaseOnExport(true);
                  pdfDialogRef.current?.showModal();
                }}
                className="hover:bg-gray-100 dark:hover:bg-base-200"
              >
                <DocumentArrowDownIcon className="size-4" />
                PDF Document
              </p>
            </li>
            <li
              onClick={() => {
                setDeleteCaseOnExport(true);
                emailDialogRef.current?.showModal();
              }}
            >
              <p className="hover:bg-gray-100 dark:hover:bg-base-200">
                <ChatBubbleBottomCenterTextIcon className="size-4" />
                E-mail
              </p>
            </li>
            {caseId && hasForms && (
              <li onClick={() => formPickerRef.current?.showModal()}>
                <p className="hover:bg-gray-100 dark:hover:bg-base-200">
                  <ClipboardDocumentListIcon className="size-4" />
                  Fill Forms
                </p>
              </li>
            )}
            {(currentTranscription || caseTranscription) &&
              isTranscribeFormat(currentTranscription || caseTranscription) && (
                <li
                  onClick={() => transcriptionPdfDialogRef.current?.showModal()}
                >
                  <p className="hover:bg-gray-100 dark:hover:bg-base-200">
                    <DocumentArrowDownIcon className="size-4" />
                    Transcription
                  </p>
                </li>
              )}
          </ul>
        </div>
      );
    } else {
      // Disabled state for non-discarded cases — show disabled Export, not Restore
      return (
        <Button
          tabIndex={0}
          data-tour="editor-export"
          className={cn(
            "fixed md:relative bottom-0 left-1/2 -translate-x-1/2 md:translate-x-0 md:left-auto md:bottom-auto",
            isMobile && "!w-full rounded-none",
          )}
          variant="primary"
          size="sm"
          disabled
        >
          <div className="flex items-center">
            <ArrowUpOnSquareIcon className="size-6 mr-1 hidden xl:inline-block" />
            <p>Export</p>
          </div>
        </Button>
      );
    }
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    editor?.commands.setSearchTerm(term);
    editor?.commands.resetIndex();
  };

  const handleReplace = (term: string) => {
    setReplaceTerm(term);
    editor?.commands.setReplaceTerm(term);
  };

  const handleNextResult = () => {
    editor?.commands.nextSearchResult();
  };

  const handlePreviousResult = () => {
    editor?.commands.previousSearchResult();
  };

  const handleToggleCaseSensitive = () => {
    const newValue = !caseSensitive;
    setCaseSensitive(newValue);
    editor?.commands.setCaseSensitive(newValue);
    // Re-trigger search with new case sensitivity
    if (searchTerm) {
      editor?.commands.setSearchTerm(searchTerm);
      editor?.commands.resetIndex();
    }
  };

  const handleReplaceOne = () => {
    editor?.commands.replace();
    // After replacing, search again to update results
    if (searchTerm) {
      editor?.commands.setSearchTerm(searchTerm);
    }
    // Trigger save to backend
    if (editor) {
      onChange({ content: { data: editor.getHTML() }, caseId });
    }
  };

  const handleReplaceAll = () => {
    editor?.commands.replaceAll();
    // After replacing all, clear search to update UI
    setSearchTerm("");
    setReplaceTerm("");
    editor?.commands.setSearchTerm("");
    editor?.commands.setReplaceTerm("");
    // Trigger save to backend
    if (editor) {
      onChange({ content: { data: editor.getHTML() }, caseId });
    }
  };

  // Pull a redaction into the Find input so the user can step through every
  // place it appears in the report. We deliberately do NOT touch the Replace
  // field — the report text is left alone; the actual blackout happens at
  // export time via the "Export with redactions" checkbox.
  const handleSelectRedactionForSearch = (r: RedactionType) => {
    handleSearch(r.text);
    // Focus the search input so Enter starts cycling matches immediately.
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  const getSearchResultsInfo = () => {
    if (!editor) return { current: 0, total: 0 };
    const storage = editor.storage.searchAndReplace;
    return {
      current: storage.results.length > 0 ? storage.resultIndex + 1 : 0,
      total: storage.results.length,
    };
  };

  return (
    <div className="sticky top-16 md:top-0 z-20 bg-gray-100 dark:bg-base-200 border-b-2">
      {/* Tighter gaps below xl so all 15 formatting controls stay on one line
          at 150% browser zoom; flex-wrap is the graceful fallback for widths
          where they genuinely can't fit, since without it the Export button
          gets pushed clean out of the viewport (the toolbar has no horizontal
          scroll and nothing clips it). */}
      <div className="flex items-center justify-between w-full gap-2 py-2 px-2">
        <div data-tour="editor-toolbar" className="flex min-w-0 flex-wrap items-center gap-0.5 xl:gap-1">
          <div className="relative inline-block" ref={dropdownRef}>
            <div>
              <IconButton
                type="button"
                id="heading-menu"
                data-tour="editor-styles"
                aria-expanded="true"
                aria-haspopup="true"
                onClick={() => setIsHeadingDropdownOpen(!isHeadingDropdownOpen)}
                description="Styles"
                disabled={isButtonDisabled}
              >
                <div className="flex items-center">
                  <span className="text-xs whitespace-nowrap">
                    {getCurrentHeading()}
                  </span>
                  <ChevronDownIcon
                    className={cn(iconBtnStyles, "ml-1 xl:ml-2")}
                    aria-hidden="true"
                  />
                </div>
              </IconButton>
            </div>

            {isHeadingDropdownOpen && (
              <div
                className="origin-top-left absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-base-100 ring-1 ring-base-content ring-opacity-5 focus:outline-none z-10"
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="heading-menu"
              >
                <div className="py-1" role="none">
                  {headingLevels.map((heading) => (
                    <button
                      key={heading.level}
                      className={`block w-full text-left px-4 py-2 text-xs prose dark:prose-invert ${
                        heading.level === 0
                          ? editor.isActive("paragraph")
                            ? "bg-gray-100 dark:bg-base-200 text-base-content"
                            : "text-gray-700 dark:text-base-content/80"
                          : editor.isActive("heading", { level: heading.level })
                            ? "bg-gray-100 dark:bg-base-200 text-base-content"
                            : "text-gray-700 dark:text-base-content/80"
                      } hover:bg-gray-100 dark:hover:bg-base-200 hover:text-base-content`}
                      role="menuitem"
                      onClick={() => applyHeading(heading.level)}
                    >
                      {heading.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="dropdown dropdown-bottom">
            <IconButton
              tabIndex={0}
              data-tour="editor-transcribe"
              disabled={isButtonDisabled}
              isActive={isRecording}
              description={
                isTranscribing
                  ? "Transcribing..."
                  : isRecording
                    ? "Stop Recording"
                    : "Transcribe Audio"
              }
              onClick={() => {
                if (isRecording) {
                  stopRecording();
                }
              }}
            >
              {isRecording ? (
                <MicrophoneIcon
                  className={cn(
                    iconBtnStyles,
                    "text-red-600 dark:text-red-400 animate-flashing",
                  )}
                />
              ) : isTranscribing ? (
                <Loader2 className={cn(iconBtnStyles, "animate-spin")} />
              ) : (
                <MicrophoneIcon className={iconBtnStyles} />
              )}
            </IconButton>
            {!isRecording && !isTranscribing && (
              <ul
                tabIndex={0}
                className="dropdown-content menu bg-base-100 rounded-lg z-[1] w-64 p-2 shadow"
              >
                {/* Audio Input Settings */}
                <div
                  className="pb-1.5 mb-1 border-b border-slate-100 dark:border-base-300 space-y-1.5"
                  style={{ maxWidth: "calc(16rem - 1rem)" }}
                >
                  <div>
                    <label className="block text-[10px] font-medium text-slate-400 dark:text-base-content/40 mb-0.5 px-0.5">
                      Microphone
                    </label>
                    {!hasPermission ? (
                      <button
                        onClick={requestPermissionAndGetDevices}
                        className="w-full text-left text-xs text-slate-400 dark:text-base-content/40 px-2.5 py-1.5 rounded-lg border border-base-300 bg-base-200 hover:border-slate-300 dark:hover:border-base-content/30"
                      >
                        Grant microphone access
                      </button>
                    ) : (
                      <select
                        className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-base-300 bg-base-200 hover:border-slate-300 dark:hover:border-base-content/30 focus:outline-none focus:border-blue-400 text-slate-700 dark:text-base-content/80 appearance-none cursor-pointer"
                        onChange={(e) => setSelectedDeviceId(e.target.value)}
                        value={selectedDeviceId}
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%2394a3b8'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z'/%3E%3C/svg%3E")`,
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "right 0.5rem center",
                          backgroundSize: "1rem",
                          paddingRight: "2rem",
                        }}
                      >
                        <option disabled value="">
                          Select microphone
                        </option>
                        {devices.map((device) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="min-w-0 overflow-hidden">
                    <label className="block text-[10px] font-medium text-slate-400 dark:text-base-content/40 mb-0.5 px-0.5">
                      Template
                    </label>
                    <TemplateSelector
                      selectedTemplateId={dictationTemplateId}
                      onSelect={setDictationTemplateId}
                      compact
                      className="min-w-0 overflow-hidden"
                      triggerClassName="w-full min-w-0 overflow-hidden flex items-center justify-between gap-2 text-xs px-2.5 py-1.5 rounded-lg border border-base-300 bg-base-200 hover:border-slate-300 dark:hover:border-base-content/30 focus:outline-none focus:border-blue-400 text-slate-700 dark:text-base-content/80 text-left cursor-pointer"
                    />
                  </div>
                </div>
                <li>
                  <button
                    onClick={() => startRecording("enhanced-insert")}
                    data-tour="dictation-enhanced"
                    className="flex items-center px-4 py-2 hover:bg-gray-100 dark:hover:bg-base-200"
                    disabled={!hasPermission || !selectedDeviceId}
                  >
                    <SparklesIcon className="size-4 text-blue-500 dark:text-blue-300" />
                    Enhanced Dictation
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => startRecording("pure-insert")}
                    data-tour="dictation-pure"
                    className="flex items-center px-4 py-2 hover:bg-gray-100 dark:hover:bg-base-200"
                    disabled={!hasPermission || !selectedDeviceId}
                  >
                    <ChatBubbleLeftEllipsisIcon className="size-4" />
                    Pure Dictation
                  </button>
                </li>
              </ul>
            )}
          </div>
          {activeTab === "transcription" ? (
            <IconButton
              onClick={onToggleTranscriptFind}
              disabled={!onToggleTranscriptFind}
              isActive={!!transcriptFindOpen}
              description="Find"
            >
              <MagnifyingGlassIcon className={iconBtnStyles} />
            </IconButton>
          ) : (
          <IconButton
            onClick={() => setShowSearchReplace(!showSearchReplace)}
            data-tour="editor-find"
            disabled={isButtonDisabled}
            isActive={showSearchReplace}
            description="Find and Replace"
          >
            <MagnifyingGlassIcon className={iconBtnStyles} />
          </IconButton>
          )}
          {onToggleProofread && (
            <IconButton
              onClick={onToggleProofread}
              data-tour="editor-proofread"
              disabled={isButtonDisabled}
              isActive={proofreadEnabled}
              description={
                proofreadEnabled
                  ? "Writing suggestions: on"
                  : "Writing suggestions: off"
              }
            >
              <DocumentCheckIcon className={iconBtnStyles} />
            </IconButton>
          )}
          <IconButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={
              isButtonDisabled ||
              !editor.can().chain().focus().toggleBold().run()
            }
            isActive={editor.isActive("bold")}
            description="Bold"
          >
            <BoldIcon className={iconBtnStyles} />
          </IconButton>
          <IconButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={
              isButtonDisabled ||
              !editor.can().chain().focus().toggleItalic().run()
            }
            isActive={editor.isActive("italic")}
            description="Italic"
          >
            <ItalicIcon className={iconBtnStyles} />
          </IconButton>
          <IconButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            disabled={
              isButtonDisabled ||
              !editor.can().chain().focus().toggleUnderline().run()
            }
            isActive={editor.isActive("underline")}
            description="Underline"
          >
            <UnderlineIcon className={iconBtnStyles} />
          </IconButton>
          <IconButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={
              isButtonDisabled ||
              !editor.can().chain().focus().toggleStrike().run()
            }
            isActive={editor.isActive("strike")}
            description="Strikethrough"
          >
            <StrikethroughIcon className={iconBtnStyles} />
          </IconButton>
          <Tooltip overlay="Text Color">
            <div className="relative inline-block">
              <IconButton
                isActive={isTextColorPickerOpen}
                disabled={isButtonDisabled}
              >
                <PencilIcon className={iconBtnStyles} />
              </IconButton>
              <input
                type="color"
                onInput={(e) => {
                  editor
                    .chain()
                    .focus()
                    .setColor((e.target as HTMLInputElement).value)
                    .run();
                }}
                onFocus={() => setIsTextColorPickerOpen(true)}
                onBlur={() => setIsTextColorPickerOpen(false)}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
            </div>
          </Tooltip>
          <Tooltip overlay="Highlight color">
            <div className="relative inline-block">
              <IconButton
                isActive={isHighlightColorPickerOpen}
                disabled={isButtonDisabled}
              >
                <PaintBrushIcon className={iconBtnStyles} />
              </IconButton>
              <input
                type="color"
                onInput={(e) => {
                  editor
                    .chain()
                    .focus()
                    .setHighlight({
                      color: (e.target as HTMLInputElement).value,
                    })
                    .run();
                }}
                onFocus={() => setIsHighlightColorPickerOpen(true)}
                onBlur={() => setIsHighlightColorPickerOpen(false)}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
            </div>
          </Tooltip>
          <IconButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            disabled={
              isButtonDisabled ||
              !editor.can().chain().focus().toggleBlockquote().run()
            }
            isActive={editor.isActive("blockquote")}
            description="Blockquote"
          >
            <ChatBubbleBottomCenterTextIcon className={iconBtnStyles} />
          </IconButton>
          <IconButton
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            disabled={
              isButtonDisabled ||
              !editor.can().chain().focus().setHorizontalRule().run()
            }
            description="Horizontal line"
          >
            <MinusIcon className={iconBtnStyles} />
          </IconButton>
          <IconButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            disabled={
              isButtonDisabled ||
              !editor.can().chain().focus().toggleBulletList().run()
            }
            isActive={editor.isActive("bulletList")}
            description="Bullet list"
          >
            <ListBulletIcon className={iconBtnStyles} />
          </IconButton>
          <IconButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            disabled={
              isButtonDisabled ||
              !editor.can().chain().focus().toggleOrderedList().run()
            }
            isActive={editor.isActive("orderedList")}
            description="Ordered list"
          >
            <NumberedListIcon className={iconBtnStyles} />
          </IconButton>
          <IconButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={
              isButtonDisabled || !editor.can().chain().focus().undo().run()
            }
            description="Undo"
          >
            <ArrowUturnLeftIcon className={iconBtnStyles} />
          </IconButton>
          <IconButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={
              isButtonDisabled || !editor.can().chain().focus().redo().run()
            }
            description="Redo"
          >
            <ArrowUturnRightIcon className={iconBtnStyles} />
          </IconButton>
        </div>
        <div className="flex shrink-0 justify-end items-center gap-1">
          {caseId && caseStatus !== "discarded" && (
            <div className="flex items-center gap-1 mr-1.5">
              <CaseCategoryMenu caseId={caseId} />
              <Tooltip
                placement="bottom"
                overlay={
                  isFavorite ? "Remove from Favorites" : "Add to Favorites"
                }
              >
                <button
                  onClick={handleToggleFavorite}
                  data-tour="favorite-star"
                  className="p-1 leading-none transition-opacity hover:opacity-60"
                >
                  {isFavorite ? (
                    <StarSolidIcon className="size-5 text-yellow-500" />
                  ) : (
                    <StarIcon className="size-5 text-base-content/40" />
                  )}
                </button>
              </Tooltip>
            </div>
          )}
          {renderActionButton()}
        </div>
      </div>

      {/* Recording Bar */}
      {(isRecording || isTranscribing) && (
        <div className="border-t-2 bg-base-100 px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left side - Recording indicator and duration */}
            <div className="flex items-center">
              {isTranscribing ? (
                <>
                  <Loader2 className="size-4 animate-spin text-blue-500" />
                  <span className="ml-3 text-sm font-medium text-gray-700 dark:text-base-content/80">
                    Transcribing...
                  </span>
                </>
              ) : (
                <>
                  <div
                    className={cn(
                      "size-3 rounded-full bg-red-500",
                      !isPaused && "animate-pulse",
                    )}
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700 dark:text-base-content/80">
                    {isPaused ? "Paused" : "Recording"}
                  </span>
                  <span className="ml-2 text-sm text-base-content/60">
                    {formatDuration(recordingDuration)}
                  </span>
                </>
              )}
            </div>

            {/* Right side - Controls */}
            {!isTranscribing && (
              <div className="flex items-center gap-2">
                {/* Cancel button */}
                <button
                  onClick={cancelRecording}
                  className="rounded-full bg-gray-100 dark:bg-base-200 p-2 hover:bg-base-300 transition-colors"
                  title="Cancel recording"
                >
                  <XMarkIcon className="size-5 text-gray-600 dark:text-base-content/70" />
                </button>

                {/* Pause/Resume button */}
                <button
                  onClick={pauseRecording}
                  className="rounded-full bg-gray-100 dark:bg-base-200 p-2 hover:bg-base-300 transition-colors"
                  title={isPaused ? "Resume recording" : "Pause recording"}
                >
                  {isPaused ? (
                    <PlayIcon className="size-5 text-gray-600 dark:text-base-content/70" />
                  ) : (
                    <PauseIcon className="size-5 text-gray-600 dark:text-base-content/70" />
                  )}
                </button>

                {/* Confirm/Done button */}
                <button
                  onClick={stopRecording}
                  className="rounded-full bg-blue-500 p-2 hover:bg-blue-600 transition-colors"
                  title="Finish recording"
                >
                  <CheckIcon className="size-5 text-white" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Find panels: the narrative's here, the transcription tab's portaled
          into the slot below so both open in the same spot */}
      <div className="border-t-2">
        <FindBar
          open={showSearchReplace && activeTab === "narrative"}
          tall={!!caseRedactions && caseRedactions.length > 0}
          inputRef={searchInputRef}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          current={getSearchResultsInfo().current}
          total={getSearchResultsInfo().total}
          onNext={handleNextResult}
          onPrevious={handlePreviousResult}
          caseSensitive={caseSensitive}
          onToggleCaseSensitive={handleToggleCaseSensitive}
          onClose={handleCloseSearch}
          replaceOpen={showReplaceInput}
          onToggleReplace={() => setShowReplaceInput(!showReplaceInput)}
        >
          {/* Redaction chips — click to populate the search input with the
              redaction's text and pre-fill Replace with "[REDACTED]" so the
              user can step through matches or one-click Replace All. */}
          {caseRedactions && caseRedactions.length > 0 && (
            <div className="flex items-start gap-1.5 mt-2 overflow-x-auto pb-1">
              <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-base-content/40 pt-1">
                Redacted:
              </span>
              <div className="flex gap-1 flex-wrap">
                {caseRedactions.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => handleSelectRedactionForSearch(r)}
                    className={cn(
                      "shrink-0 text-[11px] px-2 py-0.5 rounded-full border transition-colors max-w-[180px] truncate",
                      searchTerm === r.text
                        ? "bg-gray-800 text-white border-gray-800"
                        : "bg-base-200 hover:bg-gray-100 dark:hover:bg-base-300 border-base-300 text-gray-700 dark:text-base-content/80",
                    )}
                    title={`${r.category}${r.reason ? " — " + r.reason : ""}`}
                  >
                    <span className="font-medium">{r.text}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Replace Input Row (slides down when active) */}
          <div
            className={`overflow-hidden transition-all duration-200 ease-in-out ${
              showReplaceInput
                ? "max-h-20 opacity-100 mt-2"
                : "max-h-0 opacity-0 mt-0"
            }`}
          >
            {showReplaceInput && (
              <div className="flex items-center space-x-1">
                <div className="flex-1">
                  <input
                    ref={replaceInputRef}
                    type="text"
                    value={replaceTerm}
                    onChange={(e) => handleReplace(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleReplaceOne();
                      }
                    }}
                    placeholder="Replace with..."
                    className="input input-bordered max-h-7 w-full focus:outline-none focus:border-blue-500 border-grey-500 dark:border-base-300 transition-colors disabled:bg-grey-50 dark:disabled:bg-base-200 disabled:border-grey-500 dark:disabled:border-base-300 disabled:text-gray-600 dark:disabled:text-base-content/70 bg-base-100 text-sm px-2"
                  />
                </div>
                <Tooltip overlay="Replace (Enter)" placement="bottomRight">
                  <Button
                    variant="primary"
                    size="xs"
                    onClick={handleReplaceOne}
                    disabled={getSearchResultsInfo().total === 0}
                  >
                    <span>Replace</span>
                    <ArrowUturnLeftIcon className="size-3 rotate-180" />
                  </Button>
                </Tooltip>
                <Tooltip overlay="Replace All">
                  <Button
                    variant="outline"
                    size="xs"
                    data-tour="replace-all"
                    onClick={handleReplaceAll}
                    disabled={getSearchResultsInfo().total === 0}
                  >
                    Replace All
                  </Button>
                </Tooltip>
              </div>
            )}
          </div>
        </FindBar>
        <div ref={findSlotRef} />
      </div>

      {subscriptionStatus === SubscriptionStatusType.enum.trialing && (
        <div className="p-1 text-blue-500 underline text-center border-t-2 text-sm">
          <Link to="/subscription">
            <>{getTimeRemaining()}</>
          </Link>
        </div>
      )}
      <dialog ref={dialogRef} className="modal" data-tour="export-clipboard-dialog">
        <div className="modal-box prose w-11/12 md:w-5/12 p-8">
          <h2>Review and acknowledge</h2>
          <p>
            I affirm that this report, generated with the assistance of Police
            Narratives Al, has been thoroughly reviewed and edited by me to
            ensure its accuracy in representing my recollection of the reported
            events. I acknowledge the potential legal implications of this
            statement and, if required, am prepared to testify to its accuracy.
          </p>
          <div className="mt-4 mb-4 ">
            <Toggle
              label="Preserve Formatting"
              checked={preserveFormatting}
              onChange={(e) => setPreserveFormatting(e.target.checked)}
            />
          </div>
          {redactionExportSection}
          {deleteOnExportSection}
          <form method="dialog">
            <button className="btn absolute top-4 right-4 z-10 border-0 shadow-none">
              <XMarkIcon className="size-6" />
            </button>
            <Button
              onClick={handleCopyToClipboard}
              className="btn-primary w-full"
              variant="primary"
            >
              Agree and copy to clipboard
            </Button>
          </form>
        </div>
      </dialog>
      <dialog ref={emailDialogRef} className="modal" data-tour="export-email-dialog">
        <div className="modal-box prose w-11/12 md:w-5/12 p-8">
          <h2>Send Email</h2>
          <p className="mb-0">
            I affirm that this report, generated with the assistance of Police
            Narratives Al, has been thoroughly reviewed and edited by me to
            ensure its accuracy in representing my recollection of the reported
            events. By sending this report to my email address, I acknowledge
            the potential legal implications of this statement and, if required,
            am prepared to testify to its accuracy.
          </p>
          <div className="py-4">
            <Input
              type="email"
              value={emailValue}
              onChange={(e) => {
                setEmailValue(e.target.value);
                setEmailError("");
              }}
              placeholder="Enter email address"
              className={`!w-full !max-w-full ${
                emailError ? "border-red-500" : ""
              }`}
            />
            {emailError && (
              <p className="mt-2 text-xs text-red-500">{emailError}</p>
            )}
          </div>
          <div className="mb-4">
            <Toggle
              label="Preserve Formatting"
              checked={preserveEmailFormatting}
              onChange={(e) => setPreserveEmailFormatting(e.target.checked)}
            />
            <p className="text-xs text-base-content/60 m-0 mt-0">
              Keeps headings and bold text. Turn off for plain text (bullet and
              numbered lists are kept).
            </p>
          </div>
          {redactionExportSection}
          {deleteOnExportSection}
          <div className="modal-action">
            <Button
              variant="outline"
              onClick={() => {
                emailDialogRef.current?.close();
                setEmailValue("");
                setEmailError("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEmailExport}
              disabled={isLoading || !emailValue}
              variant="primary"
            >
              {isLoading ? "Sending..." : "Agree and send email"}
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => emailDialogRef.current?.close()}>close</button>
        </form>
      </dialog>
      {caseId && hasForms && <FormExportPickerModal modalRef={formPickerRef} caseId={caseId} />}
      <dialog ref={wordDialogRef} className="modal" data-tour="export-word-dialog">
        <div className="modal-box prose w-11/12 md:w-5/12 p-8">
          <h2>Review and acknowledge</h2>
          <p>
            I affirm that this report, generated with the assistance of Police
            Narratives Al, has been thoroughly reviewed and edited by me to
            ensure its accuracy in representing my recollection of the reported
            events. I acknowledge the potential legal implications of this
            statement and, if required, am prepared to testify to its accuracy.
          </p>
          <div className="mb-4">
            <Toggle
              label="Preserve Formatting"
              checked={preserveWordFormatting}
              onChange={(e) => setPreserveWordFormatting(e.target.checked)}
            />
            <p className="text-xs text-base-content/60 m-0 mt-0">
              Keeps headings and bold text. Turn off for plain text (bullet and
              numbered lists are kept).
            </p>
          </div>
          {redactionExportSection}
          {deleteOnExportSection}
          <form method="dialog">
            <button className="btn absolute top-4 right-4 z-10 border-0 shadow-none">
              <XMarkIcon className="size-6" />
            </button>
            <Button
              onClick={handleExportWord}
              className="btn-primary w-full"
              variant="primary"
            >
              <DocumentArrowDownIcon className="size-5" />
              Agree and export Word document
            </Button>
          </form>
        </div>
      </dialog>
      <dialog ref={pdfDialogRef} className="modal" data-tour="export-pdf-dialog">
        <div className="modal-box prose w-11/12 md:w-5/12 p-8 max-h-[90vh] overflow-y-auto">
          <h2>Export to PDF</h2>
          <p className="mb-4 text-sm">
            I affirm that this report, generated with the assistance of Police
            Narratives AI, has been thoroughly reviewed and edited by me to
            ensure its accuracy in representing my recollection of the
            reported events. I acknowledge the potential legal implications of
            this statement and, if required, am prepared to testify to its
            accuracy.
          </p>
          <div className="mb-4">
            <Toggle
              label="Include Title"
              checked={includePdfTitle}
              onChange={(e) => setIncludePdfTitle(e.target.checked)}
            />
            {includePdfTitle && (
              <div className="mt-2">
                <TextInput
                  key={`pdf-title-${resetKey}`}
                  label="Title"
                  placeholder="Enter custom title (optional)"
                  value={pdfCustomTitle}
                  onSave={(value) => setPdfCustomTitle(value)}
                  action={
                    pdfCustomTitle ||
                    (editor ? extractTitle(editor.getHTML()) : "") ||
                    "Police Report"
                  }
                  size="sm"
                />
              </div>
            )}
          </div>
          <div className="mb-4">
            <Toggle
              label="Include Case ID"
              checked={includePdfCaseId}
              onChange={(e) => setIncludePdfCaseId(e.target.checked)}
            />
            {includePdfCaseId && (
              <div className="mt-2">
                <TextInput
                  key={`pdf-case-id-${resetKey}`}
                  label="Case ID"
                  placeholder="Enter custom case ID (optional)"
                  value={pdfCustomCaseId}
                  onSave={(value) => setPdfCustomCaseId(value)}
                  action={pdfCustomCaseId || caseId || "Default Case ID"}
                  size="sm"
                />
              </div>
            )}
          </div>
          <div className="mb-4">
            <Toggle
              label="Include Date"
              checked={includePdfDate}
              onChange={(e) => setIncludePdfDate(e.target.checked)}
            />
            {includePdfDate && (
              <div className="mt-2">
                <DatePicker
                  key={`pdf-date-${resetKey}`}
                  label="Date"
                  value={pdfCustomDate}
                  onChange={setPdfCustomDate}
                  size="sm"
                />
              </div>
            )}
          </div>
          <div className="mb-4">
            <Toggle
              label="Include Generated Date &amp; Time"
              checked={includePdfGeneratedTimestamp}
              onChange={(e) =>
                setIncludePdfGeneratedTimestamp(e.target.checked)
              }
            />
            <p className="text-xs text-base-content/60 m-0 mt-0">
              Shows "Generated: {moment().format("MMMM DD, YYYY [at] h:mm A")}"
              at the bottom of every page.
            </p>
          </div>
          <div className="mb-4">
            <TextArea
              key={`pdf-header-${resetKey}`}
              label="Page Header"
              placeholder="Text shown at the top of every page (optional)"
              value={pdfCustomHeader}
              onChange={(e) => setPdfCustomHeader(e.target.value)}
              rows={3}
            />
          </div>
          <div className="mb-4">
            <TextArea
              key={`pdf-footer-${resetKey}`}
              label="Page Footer"
              placeholder="Text shown at the bottom of every page (optional)"
              value={pdfCustomFooter}
              onChange={(e) => setPdfCustomFooter(e.target.value)}
              rows={3}
            />
          </div>
          <div className="mb-4">
            <Toggle
              label="Preserve Formatting"
              checked={preservePdfFormatting}
              onChange={(e) => setPreservePdfFormatting(e.target.checked)}
            />
            <p className="text-xs text-base-content/60 m-0 mt-0">
              Keeps headings and bold text. Turn off for plain text (bullet and
              numbered lists are kept).
            </p>
          </div>
          {redactionExportSection}
          {deleteOnExportSection}
          <div className="modal-action">
            <Button
              variant="outline"
              onClick={() => pdfDialogRef.current?.close()}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExportNarrativePdf}
              disabled={isNarrativePdfExporting}
              variant="primary"
            >
              <DocumentArrowDownIcon className="size-5" />
              {isNarrativePdfExporting
                ? "Generating..."
                : "Agree and download PDF"}
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => pdfDialogRef.current?.close()}>close</button>
        </form>
      </dialog>
      <dialog ref={transcriptionPdfDialogRef} className="modal" data-tour="export-transcription-dialog">
        <div className="modal-box prose w-11/12 md:w-5/12 p-8">
          <h2>Export Transcription</h2>
          <p className="mb-4">
            Speaker labels in this transcription are generated automatically and
            may not be 100% accurate. It is the responsibility of the reviewing
            officer to verify and differentiate between speakers to ensure the
            accuracy of the narrative.
          </p>
          <p className="mb-4">
            By exporting this transcription file, you acknowledge the potential
            limitations of automated speaker identification and agree to review
            the content for accuracy.
          </p>
          <div className="mb-4">
            <TextInput
              key={`case-id-${resetKey}`}
              label="Case ID"
              placeholder="Enter custom case ID (optional)"
              value={customCaseId}
              onSave={(value) => setCustomCaseId(value)}
              action={customCaseId || caseId || "Default Case ID"}
              size="sm"
            />
          </div>
          <div className="mb-4">
            <TextInput
              key={`title-${resetKey}`}
              label="Title"
              placeholder="Enter custom title (optional)"
              value={customTitle}
              onSave={(value) => setCustomTitle(value)}
              action={
                customTitle ||
                (editor ? extractTitle(editor.getHTML()) : "") ||
                "Default Title"
              }
              size="sm"
            />
          </div>
          <div className="mb-4">
            <DatePicker
              key={`date-${resetKey}`}
              label="Date"
              value={customDate}
              onChange={setCustomDate}
              size="sm"
            />
          </div>
          <div className="mb-4">
            <Dropdown
              label="Export Format"
              options={[
                { value: "letter", label: 'PDF - Letter (8.5" x 11")' },
                { value: "legal", label: 'PDF - Legal (8.5" x 14")' },
                { value: "docx", label: "DOCX - Word Document" },
                { value: "txt", label: "TXT - Text File" },
              ]}
              value={exportTranscriptionFormat}
              onChange={(value) =>
                setExportTranscriptionFormat(
                  value as "letter" | "legal" | "txt" | "docx",
                )
              }
              className="w-full"
              size="sm"
            />
          </div>
          <div className="mb-4">
            <Toggle
              label="Include Timestamps"
              checked={includeTimestamps}
              onChange={(e) => setIncludeTimestamps(e.target.checked)}
            />
          </div>
          <div className="mb-4">
            <TextArea
              key={`header-${resetKey}`}
              label="Header"
              placeholder="Enter custom header text (optional)"
              value={customHeader}
              onChange={(e) => setCustomHeader(e.target.value)}
              rows={4}
            />
          </div>
          <div className="modal-action">
            <Button
              variant="outline"
              onClick={() => {
                transcriptionPdfDialogRef.current?.close();
                // Reset custom settings to defaults when canceling
                resetCustomSettings();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleTranscriptionPDFExport}
              disabled={isPdfExporting}
              variant="primary"
            >
              {isPdfExporting ? "Generating..." : "Download file"}
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button
            onClick={() => {
              transcriptionPdfDialogRef.current?.close();
              // Reset custom settings to defaults when clicking backdrop
              resetCustomSettings();
            }}
          >
            close
          </button>
        </form>
      </dialog>
    </div>
  );
};

export default EditorMenuBar;
